import { Button, Card, Dialog, DialogPanel, TextInput, Textarea } from "@tremor/react";
import Badge from "../components/Badge.jsx";
import {
  ExternalLink,
  Globe,
  MessageSquare,
  Pencil,
  Plus,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  createStatusPage,
  createStatusPageMessage,
  updateStatusPage,
  updateStatusPageMessage,
} from "../api/statusPages.js";
import { useMonitors } from "../queries/monitors.js";
import { useGroups } from "../queries/groups.js";
import { useCurrentWorkspace } from "../queries/workspaces.js";
import {
  useStatusPages,
  useStatusPageSubscribers,
} from "../queries/statusPages.js";
import { useQueryClient } from "@tanstack/react-query";
import { keys } from "../queryKeys.js";

import { useTranslation } from "react-i18next";

const EMPTY_FORM = {
  name: "",
  slug: "",
  headline: "",
  subheadline: "",
  logo_url: "",
  brand_color: "",
  custom_footer: "",
  is_public: true,
  show_uptime_chart: true,
  show_incident_history_days: 90,
  monitor_ids: [],
  group_ids: [],
};

const EMPTY_MESSAGE_FORM = {
  title: "",
  body: "",
  is_active: true,
  category: "general",
  severity: "info",
  publish: true,
};

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function formatTimestamp(value, i18n) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat(i18n.language, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    hour12: false,
  }).format(date) + " UTC";
}

function StatusPageEditor({ workspace, monitors, groups, page, onClose, onSaved }) {
  const { t } = useTranslation("status_pages");
  const isEdit = !!page;
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    ...page,
    monitor_ids: page?.monitor_ids ?? [],
    group_ids: page?.group_ids ?? [],
    show_incident_history_days: page?.show_incident_history_days ?? 90,
  }));
  const [saving, setSaving] = useState(false);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function toggleMonitor(id) {
    setForm((prev) => ({
      ...prev,
      monitor_ids: prev.monitor_ids.includes(id)
        ? prev.monitor_ids.filter((item) => item !== id)
        : [...prev.monitor_ids, id],
    }));
  }

  function toggleGroup(id) {
    setForm((prev) => ({
      ...prev,
      group_ids: prev.group_ids.includes(id)
        ? prev.group_ids.filter((item) => item !== id)
        : [...prev.group_ids, id],
    }));
  }

  // Monitors already covered by a selected group — hint to the user that
  // picking them individually is redundant.
  const groupedMonitorIds = new Set();
  for (const group of groups ?? []) {
    if (form.group_ids.includes(group.id)) {
      for (const m of group.members ?? []) {
        groupedMonitorIds.add(m.monitor_id ?? m.id);
      }
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const brandColor = form.brand_color?.trim() || null;
    if (brandColor && !/^#[0-9a-fA-F]{6}$/.test(brandColor)) {
      toast.error(t("errors.brand_color_invalid"));
      return;
    }
    const logoUrl = form.logo_url?.trim() || null;
    if (logoUrl && !logoUrl.startsWith("https://")) {
      toast.error(t("errors.logo_url_invalid"));
      return;
    }

    const payload = {
      name: form.name.trim(),
      slug: slugify(form.slug || form.name),
      headline: form.headline?.trim() || null,
      subheadline: form.subheadline?.trim() || null,
      logo_url: logoUrl,
      brand_color: brandColor,
      custom_footer: form.custom_footer?.trim() || null,
      is_public: form.is_public,
      show_uptime_chart: form.show_uptime_chart,
      show_incident_history_days: Number(form.show_incident_history_days),
      monitor_ids: form.monitor_ids,
      group_ids: form.group_ids,
    };

    if (!payload.name) {
      toast.error(t("errors.name_required"));
      return;
    }
    if (!payload.slug) {
      toast.error(t("errors.slug_required"));
      return;
    }
    if (payload.show_incident_history_days <= 0) {
      toast.error(t("errors.history_window_invalid"));
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateStatusPage(page.id, payload);
        toast.success(t("editor.success_save"));
      } else {
        await createStatusPage(payload);
        toast.success(t("editor.success_create"));
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error ?? t("errors.save_error"));
    } finally {
      setSaving(false);
    }
  }

  const previewUrl = page?.public_url || (workspace?.slug ? `http://${workspace.slug}.monitero.sh` : null);

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-lg flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? t("editor.title_edit") : t("editor.title_new")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">{t("editor.name")}</label>
              <TextInput
                required
                value={form.name}
                onValueChange={(value) => {
                  set("name", value);
                  if (!isEdit && (!form.slug || form.slug === slugify(form.name))) {
                    set("slug", slugify(value));
                  }
                }}
                placeholder="Acme Cloud Status"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">{t("editor.slug")}</label>
              <TextInput
                value={form.slug}
                onValueChange={(v) => set("slug", slugify(v))}
                placeholder="acme-cloud"
              />
              {previewUrl && (
                <p className="mt-1 text-xs text-gray-400">
                  {t("editor.public_url")}:{" "}
                  <a href={previewUrl} target="_blank" rel="noreferrer" className="text-teal-600 hover:underline">
                    {previewUrl}
                  </a>
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("editor.headline")} <span className="font-normal text-gray-400">{t("editor.optional")}</span>
              </label>
              <TextInput
                value={form.headline ?? ""}
                onValueChange={(v) => set("headline", v)}
                placeholder="Real-time service health for Acme Cloud"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("editor.subheadline")} <span className="font-normal text-gray-400">{t("editor.optional")}</span>
              </label>
              <Textarea
                rows={3}
                value={form.subheadline ?? ""}
                onValueChange={(v) => set("subheadline", v)}
                placeholder="Track incidents, maintenance windows, and component health in one place."
              />
            </div>

            <div className="border-t border-gray-100 pt-5">
              <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-400">{t("editor.branding_section")}</p>

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    {t("editor.logo_url")} <span className="font-normal text-gray-400">{t("editor.optional")}</span>
                  </label>
                  <TextInput
                    value={form.logo_url ?? ""}
                    onValueChange={(v) => set("logo_url", v)}
                    placeholder="https://cdn.yourcompany.com/logo.png"
                  />
                  <p className="mt-1 text-xs text-gray-400">{t("editor.logo_url_hint")}</p>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    {t("editor.brand_color")} <span className="font-normal text-gray-400">{t("editor.optional")}</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.brand_color || "#0f766e"}
                      onChange={(e) => set("brand_color", e.target.value)}
                      className="h-9 w-12 cursor-pointer rounded border border-gray-300 p-0.5"
                    />
                    <TextInput
                      value={form.brand_color ?? ""}
                      onValueChange={(v) => set("brand_color", v)}
                      placeholder="#1e40af"
                      className="flex-1"
                    />
                    {form.brand_color && (
                      <button
                        type="button"
                        onClick={() => set("brand_color", "")}
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        {t("editor.reset")}
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-400">{t("editor.brand_color_hint")}</p>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    {t("editor.custom_footer")} <span className="font-normal text-gray-400">{t("editor.optional")}</span>
                  </label>
                  <TextInput
                    value={form.custom_footer ?? ""}
                    onValueChange={(v) => set("custom_footer", v)}
                    placeholder="© 2026 Acme Corp — status.acme.com"
                  />
                  <p className="mt-1 text-xs text-gray-400">{t("editor.custom_footer_hint")}</p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.is_public}
                  onChange={(e) => set("is_public", e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
                />
                {t("editor.public_access")}
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.show_uptime_chart}
                  onChange={(e) => set("show_uptime_chart", e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
                />
                {t("editor.show_uptime")}
              </label>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("editor.history_window")}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  value={form.show_incident_history_days}
                  onChange={(e) => set("show_incident_history_days", e.target.value)}
                  className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
                />
                <span className="text-sm text-gray-500">{t("editor.days")}</span>
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Privacy & SEO
              </p>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Password (leave blank for open page)
                </label>
                <input
                  type="password"
                  value={form.password ?? ""}
                  onChange={(e) => set("password", e.target.value)}
                  placeholder={
                    form.password_protected
                      ? "•••••••• (currently set — type to change)"
                      : "Off"
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Viewers must enter this password before seeing the page.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!form.enable_private_link}
                    onChange={(e) => set("enable_private_link", e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
                  />
                  Private link (bypasses password)
                </label>
                {form.private_link && (
                  <button
                    type="button"
                    onClick={() => set("rotate_private_link", true)}
                    className="text-xs font-medium text-teal-700 hover:underline"
                  >
                    Rotate token
                  </button>
                )}
              </div>
              {form.private_link && form.enable_private_link && (
                <div className="rounded border border-gray-200 bg-white p-2 font-mono text-[11px] text-gray-700 break-all">
                  {form.private_link}
                </div>
              )}

              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={!!form.noindex}
                  onChange={(e) => set("noindex", e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
                />
                Tell search engines not to index this page (noindex)
              </label>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Custom domain (CNAME → cname.monitero.io)
                </label>
                <input
                  type="text"
                  value={form.custom_domain ?? ""}
                  onChange={(e) => set("custom_domain", e.target.value)}
                  placeholder="status.acme.com"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono outline-none focus:border-teal-600"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-xs font-medium text-gray-600">Groups</label>
              <p className="mb-2 text-xs text-gray-400">
                Selected groups render as labelled sections on the page,
                listing each group's current monitors.
              </p>
              {(!groups || groups.length === 0) ? (
                <p className="text-xs text-gray-400">No groups defined yet.</p>
              ) : (
                <div className="space-y-1">
                  {groups.map((group) => (
                    <label
                      key={group.id}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={form.group_ids.includes(group.id)}
                        onChange={() => toggleGroup(group.id)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
                      />
                      <span className="font-medium">{group.name}</span>
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                        {group.member_count ?? group.members?.length ?? 0} monitor
                        {(group.member_count ?? group.members?.length ?? 0) === 1 ? "" : "s"}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="mb-2 block text-xs font-medium text-gray-600">{t("editor.components")}</label>
              <p className="mb-2 text-xs text-gray-400">
                Individual monitors shown outside any selected group.
              </p>
              {monitors.length === 0 ? (
                <p className="text-xs text-gray-400">{t("editor.no_monitors")}</p>
              ) : (
                <div className="space-y-1">
                  {monitors.map((monitor) => {
                    const inGroup = groupedMonitorIds.has(monitor.id);
                    return (
                      <label
                        key={monitor.id}
                        className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-gray-50 ${
                          inGroup ? "text-gray-400" : "text-gray-700"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.monitor_ids.includes(monitor.id)}
                          onChange={() => toggleMonitor(monitor.id)}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
                        />
                        <span className="font-medium">{monitor.name}</span>
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-500">
                          {monitor.type}
                        </span>
                        {inGroup && (
                          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">
                            already in a selected group
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="secondary" type="button" onClick={onClose}>{t("common:common.cancel")}</Button>
            <Button type="submit" disabled={saving}>
              {saving ? t("common:common.saving") : isEdit ? t("editor.save") : t("editor.create")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

function StatusMessageEditor({ pageId, message, onClose, onSaved }) {
  const { t } = useTranslation("status_pages");
  const isEdit = !!message;
  const [form, setForm] = useState(() => ({
    ...EMPTY_MESSAGE_FORM,
    ...message,
  }));
  const [saving, setSaving] = useState(false);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const payload = {
      title: form.title.trim(),
      body: form.body.trim(),
      is_active: form.is_active,
      category: form.category || "general",
      severity: form.severity || "info",
      publish: form.publish ?? true,
    };

    if (!payload.title) {
      toast.error(t("errors.message_title_required"));
      return;
    }
    if (!payload.body) {
      toast.error(t("errors.message_body_required"));
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateStatusPageMessage(pageId, message.id, payload);
        toast.success(t("messages.success_update"));
      } else {
        await createStatusPageMessage(pageId, payload);
        toast.success(t("messages.success_save"));
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error ?? t("errors.save_error"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-w-lg flex-col rounded-none bg-white p-0 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? t("message_editor.title_edit") : t("message_editor.title_new")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">{t("message_editor.field_title")}</label>
              <TextInput
                value={form.title}
                onValueChange={(v) => set("title", v)}
                placeholder={t("message_editor.field_title_placeholder")}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">{t("message_editor.field_body")}</label>
              <Textarea
                rows={6}
                value={form.body}
                onValueChange={(v) => set("body", v)}
                placeholder={t("message_editor.field_body_placeholder")}
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Phase</label>
                <select
                  value={form.category ?? "general"}
                  onChange={(e) => set("category", e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
                >
                  <option value="general">General update</option>
                  <option value="investigating">Investigating</option>
                  <option value="identified">Identified</option>
                  <option value="monitoring">Monitoring</option>
                  <option value="resolved">Resolved</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Severity</label>
                <select
                  value={form.severity ?? "info"}
                  onChange={(e) => set("severity", e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
                >
                  <option value="info">Info</option>
                  <option value="minor">Minor</option>
                  <option value="major">Major</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => set("is_active", e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
              />
              {t("message_editor.show_public")}
            </label>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.publish ?? true}
                onChange={(e) => set("publish", e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600"
              />
              Publish now (uncheck to save as draft)
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="secondary" type="button" onClick={onClose}>{t("common:common.cancel")}</Button>
            <Button type="submit" disabled={saving}>
              {saving ? t("common:common.saving") : isEdit ? t("message_editor.save") : t("message_editor.create")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

function MessageList({ page, onCreate, onEdit }) {
  const { t, i18n } = useTranslation("status_pages");
  const messages = page?.messages ?? [];

  return (
    <Card className="p-0">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{t("messages.title")}</h2>
          <p className="mt-1 text-xs text-gray-400">
            {t("messages.subtitle")}
          </p>
        </div>
        <Button icon={Plus} onClick={onCreate}>{t("messages.add")}</Button>
      </div>

      {messages.length === 0 ? (
        <div className="px-5 py-8 text-center">
          <MessageSquare className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <p className="text-sm font-medium text-gray-700">{t("messages.empty_title")}</p>
          <p className="mt-1 text-sm text-gray-400">
            {t("messages.empty_description")}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {messages.map((message) => (
            <div key={message.id} className="px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900">{message.title}</p>
                    <Badge color={message.is_active ? "amber" : "gray"}>
                      {message.is_active ? t("messages.visible") : t("messages.hidden")}
                    </Badge>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-gray-600">{message.body}</p>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                    <span>{t("messages.created", { date: formatTimestamp(message.created_at, i18n) })}</span>
                    {message.created_by_name && <span>{t("messages.by", { name: message.created_by_name })}</span>}
                    <span>{t("messages.updated", { date: formatTimestamp(message.updated_at, i18n) })}</span>
                  </div>
                </div>
                <Button variant="secondary" size="xs" icon={Pencil} onClick={() => onEdit(message)}>
                  {t("common:common.edit")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function SubscriberList({ subscribers }) {
  const { t, i18n } = useTranslation("status_pages");
  return (
    <Card className="p-0">
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-sm font-semibold text-gray-900">{t("subscribers.title")}</h2>
        <p className="mt-1 text-xs text-gray-400">
          {t("subscribers.subtitle")}
        </p>
      </div>
      {subscribers.length === 0 ? (
        <p className="px-5 py-6 text-sm text-gray-400">{t("subscribers.empty")}</p>
      ) : (
        <div className="divide-y divide-gray-50">
          {subscribers.map((subscriber) => (
            <div key={subscriber.id} className="px-5 py-3">
              <div className="text-sm font-medium text-gray-900">{subscriber.email}</div>
              <div className="mt-1 text-xs text-gray-400">
                {t("subscribers.confirmed", { date: formatTimestamp(subscriber.confirmed_at, i18n) })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function StatusPages() {
  const { t } = useTranslation(["status_pages", "common"]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [messageEditorOpen, setMessageEditorOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);

  const qc = useQueryClient();
  const workspaceQuery = useCurrentWorkspace();
  const monitorsQuery = useMonitors({ limit: 100 });
  const groupsQuery = useGroups();
  const statusPagesQuery = useStatusPages();
  const statusPages = statusPagesQuery.data ?? [];
  const page = statusPages[0] ?? null;
  const subscribersQuery = useStatusPageSubscribers(page?.id);
  const workspace = workspaceQuery.data;
  const monitors = monitorsQuery.data?.data ?? [];
  const groups = groupsQuery.data ?? [];
  const subscribers = subscribersQuery.data ?? [];

  const loading =
    workspaceQuery.isPending ||
    monitorsQuery.isPending ||
    statusPagesQuery.isPending;

  useEffect(() => {
    if (workspaceQuery.isError || statusPagesQuery.isError) {
      toast.error(t("errors.load_error"));
    }
  }, [workspaceQuery.isError, statusPagesQuery.isError, t]);

  // Used by editor callbacks to refetch after save.
  const reload = () => {
    qc.invalidateQueries({ queryKey: keys.statusPages.all });
  };
  const selectedMonitors = monitors.filter((monitor) => page?.monitor_ids?.includes(monitor.id));
  const publicUrl = page?.public_url || (workspace?.slug ? `http://${workspace.slug}.localhost:8000` : null);

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
        <p className="mt-4 text-sm text-gray-400">{t("loading")}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
        </div>
        <Button
          icon={page ? Pencil : Plus}
          onClick={() => {
            setEditingPage(page);
            setEditorOpen(true);
          }}
        >
          {page ? t("common:common.edit") : t("new_page")}
        </Button>
      </div>

      {page && publicUrl && (
        <Card className="mb-6 flex items-center gap-3 py-3 px-4">
          <Globe className="h-4 w-4 shrink-0 text-gray-400" />
          <span className="text-sm text-gray-500">{t("url.label")}</span>
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-teal-600 hover:text-teal-700"
          >
            {publicUrl}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          {!page.is_public && (
            <Badge color="gray" className="ml-auto">{t("url.hidden")}</Badge>
          )}
        </Card>
      )}

      {!page ? (
        <Card className="px-5 py-12 text-center">
          <Globe className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <p className="text-sm font-medium text-gray-700">{t("empty.title")}</p>
          <p className="mt-1 text-sm text-gray-400">
            {t("empty.description")}
          </p>
          <Button
            className="mt-4"
            icon={Plus}
            onClick={() => {
              setEditingPage(null);
              setEditorOpen(true);
            }}
          >
            {t("new_page")}
          </Button>
        </Card>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-5">
            <Card className="p-0">
              <div className="border-b border-gray-100 px-5 py-4">
                <h2 className="text-sm font-semibold text-gray-900">{t("settings.title")}</h2>
              </div>
              <div className="divide-y divide-gray-50 px-5">
                {[
                  { label: t("settings.name"), value: page.name },
                  {
                    label: t("settings.visibility"),
                    value: page.is_public ? (
                      <span className="font-medium text-green-600">{t("settings.public")}</span>
                    ) : (
                      <span className="text-gray-500">{t("settings.hidden")}</span>
                    ),
                  },
                  { label: t("settings.uptime_chart"), value: page.show_uptime_chart ? t("settings.visible") : t("settings.hidden") },
                  { label: t("settings.incident_history"), value: t("settings.history_days", { count: page.show_incident_history_days }) },
                  { label: t("settings.headline"), value: page.headline || <span className="text-gray-400">{t("settings.not_set")}</span> },
                  { label: t("settings.subheadline"), value: page.subheadline || <span className="text-gray-400">{t("settings.not_set")}</span> },
                  {
                    label: t("settings.brand_color"),
                    value: page.brand_color ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block h-4 w-4 rounded border border-gray-200" style={{ background: page.brand_color }} />
                        <span className="font-mono text-xs">{page.brand_color}</span>
                      </span>
                    ) : <span className="text-gray-400">{t("settings.not_set")}</span>,
                  },
                  { label: t("settings.logo_url"), value: page.logo_url ? <a href={page.logo_url} target="_blank" rel="noreferrer" title={page.logo_url} className="inline-block max-w-[260px] truncate align-bottom text-teal-600 hover:underline">{page.logo_url}</a> : <span className="text-gray-400">{t("settings.not_set")}</span> },
                  { label: t("settings.custom_footer"), value: page.custom_footer || <span className="text-gray-400">{t("settings.not_set")}</span> },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-start justify-between gap-3 py-3">
                    <span className="shrink-0 text-xs font-medium text-gray-500">{label}</span>
                    <span className="min-w-0 break-words text-right text-sm text-gray-900">{value}</span>
                  </div>
                ))}
              </div>
            </Card>

            <MessageList
              page={page}
              onCreate={() => {
                setEditingMessage(null);
                setMessageEditorOpen(true);
              }}
              onEdit={(message) => {
                setEditingMessage(message);
                setMessageEditorOpen(true);
              }}
            />

            <SubscriberList subscribers={subscribers} />
          </div>

          <div className="space-y-5">
            <Card className="p-0">
              <div className="border-b border-gray-100 px-5 py-4">
                <h2 className="text-sm font-semibold text-gray-900">
                  {t("components.title", { count: selectedMonitors.length })}
                </h2>
              </div>
              {selectedMonitors.length === 0 ? (
                <p className="px-5 py-6 text-sm text-gray-400">
                  {t("components.empty")}
                </p>
              ) : (
                <div className="divide-y divide-gray-50">
                  {selectedMonitors.map((monitor) => (
                    <div key={monitor.id} className="flex items-center justify-between px-5 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{monitor.name}</p>
                        <p className="text-xs text-gray-400">{monitor.target}</p>
                      </div>
                      <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600">
                        {monitor.type}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      {editorOpen && (
        <StatusPageEditor
          workspace={workspace}
          monitors={monitors}
          groups={groups}
          page={editingPage}
          onClose={() => setEditorOpen(false)}
          onSaved={() => {
            setEditorOpen(false);
            reload();
          }}
        />
      )}

      {messageEditorOpen && page && (
        <StatusMessageEditor
          pageId={page.id}
          message={editingMessage}
          onClose={() => setMessageEditorOpen(false)}
          onSaved={() => {
            setMessageEditorOpen(false);
            setEditingMessage(null);
            reload();
          }}
        />
      )}
    </div>
  );
}
